import { HorizontalScrollView } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import {
  type ExtraWorkedTimeRequestEntity,
  type ExtraWorkedTimeRequestShift,
  type ExtraWorkedTimeRequestStatus,
  ExtraWorkedTimeRequestStatus as ExtraWorkedTimeRequestStatusEnum,
  type ExtraWorkedTimeRequestStatusUpdater,
} from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { compareDesc, parseISO } from "date-fns";
import { useMemo } from "react";

import { useGetExtraWorkedTimeRequests } from "../api/useGetExtraWorkedTimeRequests";
import { useDismissedExtraWorkedTimeRequests } from "../hooks/useDismissedExtraWorkedTimeRequests";
import { ExtraWorkedTimeRequestBanner } from "./ExtraWorkedTimeRequestBanner";

interface TransformedRequest {
  id: string;
  status: ExtraWorkedTimeRequestStatus.APPROVED | ExtraWorkedTimeRequestStatus.DECLINED;
  workplaceName: string;
  shiftDate: Date;
  shiftId: string;
  updatedAt: string;
  isVerified: boolean;
  isFullShiftPayment?: boolean;
  statusUpdatedBy?: ExtraWorkedTimeRequestStatusUpdater;
}

export function ExtraWorkedTimeRequestBannerList() {
  const { data, isLoading, isError } = useGetExtraWorkedTimeRequests();

  // Extract request IDs from API response for localStorage cleanup
  // Pass undefined when data hasn't loaded to avoid premature cleanup
  const requestIds = useMemo(() => data?.data.map(({ id }) => id), [data]);

  const { isRequestDismissed, dismissRequest } = useDismissedExtraWorkedTimeRequests(requestIds);

  const requests = useMemo((): TransformedRequest[] => {
    if (!data?.data) {
      return [];
    }

    const shiftMap = new Map<string, ExtraWorkedTimeRequestShift>(
      data.included
        .filter((item): item is ExtraWorkedTimeRequestShift => item.type === "shift")
        .map((shift) => [shift.id, shift])
    );

    // Sort by updatedAt descending (most recent first) and deduplicate by shiftId
    const deduplicatedRequests = [
      ...data.data
        .toSorted((a, b) =>
          compareDesc(parseISO(a.attributes.updatedAt), parseISO(b.attributes.updatedAt))
        )
        .reduce((map, request) => {
          if (!map.has(request.attributes.shiftId)) {
            map.set(request.attributes.shiftId, request);
          }
          return map;
        }, new Map<string, ExtraWorkedTimeRequestEntity>())
        .values(),
    ];

    return deduplicatedRequests
      .filter(
        ({ attributes: { status }, id }) =>
          status !== ExtraWorkedTimeRequestStatusEnum.PENDING && !isRequestDismissed(id)
      )
      .map(
        ({
          attributes: { shiftId, status, isFullShiftPayment, updatedAt, statusUpdatedBy },
          id,
        }) => {
          const shift = shiftMap.get(shiftId);
          if (!shift) {
            return undefined;
          }

          const { facility, start, verified } = shift.attributes;

          return {
            id,
            status: status as TransformedRequest["status"],
            workplaceName: facility.name,
            shiftDate: parseISO(start),
            shiftId,
            updatedAt,
            isVerified: verified,
            isFullShiftPayment,
            statusUpdatedBy,
          };
        }
      )
      .filter(isDefined);
  }, [data, isRequestDismissed]);

  if (isLoading || isError || requests.length === 0) {
    return null;
  }

  return (
    <Box sx={{ marginBottom: 6, marginX: -5 }}>
      <HorizontalScrollView stretch sx={{ px: 5 }}>
        {requests.map((request) => (
          <ExtraWorkedTimeRequestBanner
            key={request.id}
            status={request.status}
            workplaceName={request.workplaceName}
            shiftDate={request.shiftDate}
            shiftId={request.shiftId}
            updatedAt={request.updatedAt}
            isVerified={request.isVerified}
            isFullShiftPayment={request.isFullShiftPayment}
            statusUpdatedBy={request.statusUpdatedBy}
            onDismiss={() => {
              dismissRequest(request.id);
            }}
          />
        ))}
      </HorizontalScrollView>
    </Box>
  );
}
